import {memo, type RefObject} from "react";
import {Bell, Crown, SlidersHorizontal} from "lucide-react";
import {NavIconButton} from "../ui";

type HeaderProps = {
    unreadNotifications: number;
    isPremium: boolean;
    superLikesRemaining: number;
    onOpenFilters: () => void;
    onOpenNotifications: () => void;
    onOpenSuperLikeShop: () => void;
    onOpenSubscription: () => void;
    onOpenHome: () => void;
    // RefObject<T> в типах React 18 уже допускает null в current; писать
    // RefObject<HTMLDivElement | null> нельзя — такой ref не принимает <div ref={...}>.
    notificationsAnchorRef?: RefObject<HTMLDivElement>;
};

// Приглушённый язык компактных кнопок шапки — тот же принцип, что у
// `.rla-nav-btn` в нижней навигации (styles/app.css): глухой нейтральный
// фон вместо сплошной белой заливки с тенью, без бренд-цвета на самой
// кнопке (бренд остаётся только на бейдже-акценте). Здесь фон при этом не
// прозрачный, как в тёмной пилюле навигации, а взят по образцу `.rla-iconbtn`
// (тоже styles/app.css) — тот вариант рассчитан на тёплую поверхность шапки
// (см. ниже, баг 22.08: шапка была `bg-white`, спорила с тёплым фоном
// остальных экранов — Ирина прислала скрины). `[&_svg]:size-4` перебивает
// жёстко заданный в NavIconButton размер иконки (`size-6 sm:size-7`) — это
// тот же приём с более высокой специфичностью, что уже используется в
// базовых классах `buttonVariants` (button.tsx), поэтому переопределение
// отсюда безопасно и предсказуемо.
//
// `rla-hit44` (styles/app.css) добавляет невидимую зону нажатия 44×44 вокруг
// кнопки, НЕ меняя её вид: 32/36 px здесь — не оплошность, а прямое следствие
// требования Ирины от 21.08 «высоту хэдера уменьшить» (см. комментарий ниже),
// и увеличивать сам кружок нельзя — шапка вырастет обратно. Псевдоэлемент
// решает обе задачи сразу: вид и высота шапки прежние, палец получает 44 px.
const HEADER_ICON_BTN_CLASS =
    "rla-hit44 h-8 w-8 sm:h-9 sm:w-9 bg-black/5 hover:bg-black/[0.08] active:scale-95 [&_svg]:size-4";

export const Header = memo(function Header(
    {
        unreadNotifications,
        isPremium,
        superLikesRemaining,
        onOpenFilters,
        onOpenNotifications,
        onOpenSuperLikeShop,
        onOpenSubscription,
        onOpenHome,
        notificationsAnchorRef,
    }: HeaderProps) {
    return (
        <header
            className="relative flex-shrink-0 bg-[var(--rla-bg)] shadow-xl safe-top">
            {/* 21.08 Ирина: «Высоту хэдера в мобильном приложении надо уменьшить по
                возможности». Вертикальный padding — единственный безопасный рычаг здесь:
                логотип (35px, уже уменьшен в PR #200 по её же явной команде «−30%») и
                иконки-кнопки (32px, HEADER_ICON_BTN_CLASS) не трогаем — визуально они ниже
                привычного минимума тач-таргета ~40px, дальше сжимать их не следует; зона
                нажатия при этом доведена до 44px классом `rla-hit44`, без роста высоты шапки.
                py-2→py-1 / sm:py-2.5→sm:py-1.5 даёт итоговую высоту шапки 51px→43px на
                мобильном вьюпорте — это совпадает с классической высотой iOS nav bar
                (44pt), не случайное число. */}
            <div
                /* py-[var(--rla-chrome-gap)] вместо py-1/sm:py-1.5: то же самое число
                   (4px / 6px от 640px, см. --rla-chrome-gap в app.css), но теперь через
                   переменную — её же переиспользует нижний паддинг ленты (Feed.tsx),
                   чтобы «отступ сверху до матрёшки» и «отступ снизу перед тап-баром»
                   были буквально одним и тем же числом, а не двумя похожими (Ирина,
                   29.09, «мобильный обзор», п. 3). */
                className="max-w-[500px] mx-auto px-4 pt-0 pb-[var(--rla-chrome-gap)] sm:px-6 flex justify-between items-center">
                <button
                    type="button"
                    aria-label="На главную"
                    onClick={onOpenHome}
                    className="flex items-center gap-1.5 sm:gap-2 min-w-0 text-left"
                >
                    {/* внешний след 35px не меняется — не откатывает высоту шапки из PR #200 */}
                    <span className="rla-brandmark-badge w-[35px] h-[35px] flex-shrink-0">
                        <img
                            src="/Brand/Matreshka.png"
                            alt="Матрешка"
                            className="w-[29px] h-[29px] object-contain"
                        />
                    </span>
                    <div className="min-w-0">
                        <h1
                            className="text-lg font-bold bg-gradient-to-r from-red-600 to-amber-500 bg-clip-text text-transparent">
                            Любить по-russки
                        </h1>
                    </div>
                </button>
                <div className="flex items-center justify-self-end gap-1 sm:gap-1.5 flex-shrink-0">
                    <NavIconButton
                        icon={SlidersHorizontal}
                        label="Фильтры"
                        title="Фильтры"
                        onClick={onOpenFilters}
                        className={`${HEADER_ICON_BTN_CLASS}`}
                    />
                    <NavIconButton
                        icon={Crown}
                        label="Подписка"
                        title="Подписка"
                        onClick={onOpenSubscription}
                        className={`${HEADER_ICON_BTN_CLASS} text-[var(--rla-brand-from)]`}
                    />
                    <div ref={notificationsAnchorRef}>
                        <NavIconButton
                            icon={Bell}
                            label="Открыть уведомления"
                            title="Уведомления"
                            onClick={onOpenNotifications}
                            className={`${HEADER_ICON_BTN_CLASS} text-[var(--rla-ink-2)]`}
                            badge={unreadNotifications > 0 ? (
                                <span
                                    className="absolute top-0.5 right-0.5 min-w-[7px] size-[7px] sm:min-w-2 sm:size-2 rounded-full bg-red-500 ring-1 ring-white"/>
                            ) : undefined}
                        />
                    </div>
                </div>
            </div>
        </header>
    );
});
